import type { CoachingPractice } from '@mlm/shared';

export type RitualStepKey =
  | 'energy'
  | 'rhythm'
  | 'breathe'
  | 'listen'
  | 'affirm'
  | 'visualize'
  | 'gratitude'
  | 'intention'
  | 'action';

export type RitualStep = {
  key: RitualStepKey;
  /** Short name for the step rail. */
  label: string;
  title: string;
  body: string;
  /** Suggested time. The member can always continue sooner. */
  seconds: number;
  /** A practice the step completes, counted toward the Success Score. */
  practice?: CoachingPractice;
};

/** The blueprint's five-minute ritual (Part III §8), in seconds per step. */
const FIVE_MINUTES: Record<RitualStepKey, number> = {
  energy: 40,
  rhythm: 30,
  breathe: 45,
  listen: 60,
  affirm: 45,
  visualize: 90,
  gratitude: 45,
  intention: 30,
  action: 45,
};

const COPY: Record<RitualStepKey, Omit<RitualStep, 'key' | 'seconds'>> = {
  energy: {
    label: "Today's energy",
    title: "Today's energy",
    body: 'Notice the tone of the day before you plan it.',
  },
  rhythm: {
    label: 'Your rhythm',
    title: "Today's rhythm",
    body: 'Notice where your energy sits today and plan your hardest work around it.',
    practice: 'biorhythm',
  },
  breathe: {
    label: 'Breathe',
    title: 'Breathe',
    body: 'Slow down your mind and become fully present.',
    practice: 'breathwork',
  },
  listen: {
    label: 'Listen',
    title: 'Listen',
    body: 'Let a calm track set the tone while you settle into the day.',
    practice: 'frequency',
  },
  affirm: {
    label: 'Affirm',
    title: 'Affirm',
    body: 'Say it slowly, three times, as if it is already true.',
    practice: 'affirmation',
  },
  visualize: {
    label: 'See it',
    title: 'See it',
    body: 'Imagine yourself already living with the confidence, discipline, and opportunities you are working toward.',
    practice: 'visualization',
  },
  gratitude: {
    label: 'Gratitude',
    title: 'Feel gratitude',
    body: "What are three things you're grateful for right now?",
    practice: 'gratitude',
  },
  intention: {
    label: 'Intention',
    title: 'Choose who you will be today',
    body: 'A word or a sentence is enough.',
  },
  action: {
    label: 'Action',
    title: 'Turn intention into action',
    body: 'The one step that moves your goal forward today.',
  },
};

/**
 * The member's morning ritual (SC-14) in the blueprint's order: Breathe, Affirm,
 * See it, Gratitude, Intention, Action. Today's energy leads when they chose
 * astrology and have a chart, their rhythm follows when they chose biorhythm and
 * gave a birth date, and a listening step joins the breathing when they chose
 * frequencies. Intention and Action always run. Times scale from the
 * blueprint's five-minute ritual to the minutes they chose.
 */
export function morningSteps(
  prefs: { minutes?: number; modules?: string[] },
  birth: { hasBirthDetails: boolean; hasBirthDate: boolean },
): RitualStep[] {
  const modules = new Set(prefs.modules ?? []);
  const keys: RitualStepKey[] = [];
  if (modules.has('astrology') && birth.hasBirthDetails) keys.push('energy');
  if (modules.has('biorhythm') && birth.hasBirthDate) keys.push('rhythm');
  if (modules.has('breathwork')) keys.push('breathe');
  if (modules.has('frequencies')) keys.push('listen');
  if (modules.has('affirmations')) keys.push('affirm');
  if (modules.has('visualization')) keys.push('visualize');
  if (modules.has('gratitude')) keys.push('gratitude');
  keys.push('intention', 'action');
  const scale = Math.max(1, (prefs.minutes ?? 5) / 5);
  return keys.map((key) => ({ ...COPY[key], key, seconds: Math.round(FIVE_MINUTES[key] * scale) }));
}
